/**
 * Format an unknown error value (typically thrown from a mutation) into a
 * human-readable string suitable for a toast description.
 *
 * Handles:
 *  - `Error` instances (uses `.message`)
 *  - plain strings and numbers
 *  - DRF-style `{ detail: "..." }` / `{ message: "..." }`
 *  - DRF-style `{ non_field_errors: ["..."] }`
 *  - DRF field-error maps, e.g.
 *      { device_mac_address: ["This field must be unique."] }
 *    → "device_mac_address: This field must be unique."
 *  - Nested arrays/objects (e.g. `buttons: [{ extension: ["required"] }]`)
 *  - Arbitrary objects (falls back to JSON.stringify)
 *
 * Always returns a non-empty string. Never returns "[object Object]".
 */
export function formatApiError(error: unknown): string {
  const formatted = formatValue(error)
  if (formatted && formatted.trim().length > 0) return truncate(formatted)
  return 'An unknown error occurred'
}

/**
 * Wrap an arbitrary thrown value in a proper `Error` instance.
 *
 * Hooks should `throw toApiError(response.data, 'Fallback message')` rather
 * than `throw response.data` so that React Query always stores an `Error`
 * object with a human-readable `.message`.
 */
export function toApiError(data: unknown, fallbackMessage: string): Error {
  if (data instanceof Error) return data
  const message = formatValue(data)
  return new Error(
    message && message.trim().length > 0 ? truncate(message) : fallbackMessage,
  )
}

const MAX_LENGTH = 500

function formatValue(value: unknown): string {
  if (value == null) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }
  if (value instanceof Error) return value.message || value.name || ''

  if (typeof Response !== 'undefined' && value instanceof Response) {
    return value.statusText
      ? `${value.status} ${value.statusText}`
      : `HTTP ${value.status}`
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => formatValue(item))
      .filter((s) => s.length > 0)
      .join(', ')
  }

  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>

    if (typeof obj.detail === 'string') return obj.detail
    if (typeof obj.message === 'string') return obj.message

    if (Array.isArray(obj.non_field_errors)) {
      const joined = formatValue(obj.non_field_errors)
      if (joined) return joined
    }

    const lines: string[] = []
    for (const [field, raw] of Object.entries(obj)) {
      const inner = formatValue(raw)
      if (!inner) continue
      lines.push(`${field}: ${inner}`)
    }
    if (lines.length > 0) return lines.join('\n')

    try {
      const json = JSON.stringify(value)
      if (json && json !== '{}') return json
    } catch {
      // Fall through to empty string.
    }
  }

  return ''
}

function truncate(text: string): string {
  if (text.length <= MAX_LENGTH) return text
  return `${text.slice(0, MAX_LENGTH - 1)}…`
}
